import { DoneOutlined as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import { IconButton, Paper, Popover, Popper } from '@mui/material'
import { ScoreTooltip } from '../../api/models'
import styles from './ScoreDetailsPopover.module.css'

type ScoreDetailsPopoverProps = {
  open?: boolean
  anchorEl: HTMLElement | null
  tooltip: ScoreTooltip
  onClose: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  disablePointerEvents?: boolean
  hoverMode?: boolean
  notApplicable?: boolean
  notApplicableMessage?: string
}

const Row = ({ text, codes }: { text: string; codes?: string[] }) => (
  <div className={styles.row}>
    <CheckIcon className={styles.checkIcon} />
    <span>
      {text}
      {codes && codes.length > 0 && (
        <span className={styles.codes}> ({codes.join(', ')})</span>
      )}
    </span>
  </div>
)

export const ScoreDetailsPopover = ({
  open,
  anchorEl,
  tooltip,
  onClose,
  onMouseEnter,
  onMouseLeave,
  disablePointerEvents = false,
  hoverMode = false,
  notApplicable = false,
  notApplicableMessage = 'Score not calculated for micro-learnings in status Not Processed',
}: ScoreDetailsPopoverProps) => {
  const experimentCount = tooltip.experiment_codes.length
  const hasAllSales = tooltip.without_sales_evidence.length === 0
  const isOpen = open ?? Boolean(anchorEl)

  const popoverContent = (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Score Details</h3>
        <IconButton size="small" onClick={onClose} className={styles.closeButton}>
          <CloseIcon className={styles.closeIcon} />
        </IconButton>
      </div>

      <div className={styles.content}>
        {notApplicable ? (
          <div className={styles.row}>
            <span>{notApplicableMessage}</span>
          </div>
        ) : (
          <>
            <Row
              text={`${experimentCount} completed experiment${experimentCount !== 1 ? 's' : ''}`}
              codes={tooltip.experiment_codes}
            />

            <Row
              text={hasAllSales ? 'Has sales lift data' : 'Does not have sales lift data'}
              codes={hasAllSales ? undefined : tooltip.without_sales_evidence}
            />

            {tooltip.kpi_mismatch && (
              <Row text="KPI mismatch detected" />
            )}

            {tooltip.missing_quantitative_results.length > 0 && (
              <Row
                text="Missing quantitative results"
                codes={tooltip.missing_quantitative_results}
              />
            )}
          </>
        )}
      </div>
    </div>
  )

  if (hoverMode) {
    return (
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement='bottom-start'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          },
        ]}>
        <Paper
          elevation={0}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          sx={{
            borderRadius: '8px',
            boxShadow: '0px 1px 3px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.3)',
            pointerEvents: disablePointerEvents ? 'none' : 'auto',
          }}>
          {popoverContent}
        </Paper>
      </Popper>
    )
  }

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          elevation: 0,
          onMouseEnter,
          onMouseLeave,
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 1px 3px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.3)',
            pointerEvents: disablePointerEvents ? 'none' : 'auto',
          }
        }
      }}
    >
      {popoverContent}
    </Popover>
  )
}
